import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '../../../constants';
import { CompositionConfig } from '../../types/CompositionConfig';
import { TrackType } from '../../types/Track/Type';
import { SegmentsTrack } from '../../types/Track/SegmentsTrack';
import { Track } from '../../types/Track/Track';
import { createMainCompositeSegment } from './compositeSegment/compositeSegment';
import { createIntroVideoSegment } from './videoSegment/introVideoSegment';
import { createOutroVideoSegment } from './videoSegment/outroVideoSegment';
import { SegmentType } from '../../types/Segment/Type';
import { CompositeSegment } from '../../types/Segment/CompositeSegment';
import { getFontObject } from './compositeSegment/utils';
import { ElementsTrack } from '../../types/Track/ElementsTrack';
import { mapEffectsToPlayableTimeline } from '../animation/effectUtils';
import { mapScenesToPlayableTimeline } from '../animation/scenesUtils';
import { getPlayerConfigElements } from '../../ClipPlayerV2WithNewTimeline/utils/playerConfigGetters';
import { getPlayerConfigWithSplits } from './configWithSplits';
import { MAIN_TRACK_ID } from './constants';
import { CaptionTrackMetadata } from '../../types/Track/CaptionsTrack/CaptionsTrack';
import { canUseBrowserlessCaptions, currentClip, isSplitsInTimelineEnabled } from '@/stores/clip';
import { Clip } from '@/domains/asset';
import { getClipFPS, isClipAudiogram, isFullLengthClip, isNotFullLengthClip } from '@/libs/clipUtils';
import { SceneChanges } from '@/domains/event';
import { SelectionElementType } from '@/App/remotion/components/Outlines/types';
import { buildCaptionMetadata, getCaptionPages } from '@/App/remotion/libs/caption/caption';
import { updatePlayerConfigWithDeletes } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { Page } from '@/App/remotion/types';

export function getPlayerConfig({
  clip,
  sceneOverrides,
  shouldUpdatePlayerConfig,
  facialRecognitionToDefaultPosition
}: {
  clip: Clip;
  /**
   * This field is for creation of scenes from the event app. To be used only during initialization of the player config
   */
  sceneOverrides?: SceneChanges;
  shouldUpdatePlayerConfig?: boolean;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
}): CompositionConfig {
  if (!shouldUpdatePlayerConfig) {
    return clip.asset_metadata.player_config as CompositionConfig;
  }

  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);
  if (isSplitsEnabled) {
    return getPlayerConfigWithSplits({
      clip,
      sceneOverrides,
      shouldUpdatePlayerConfig,
      facialRecognitionToDefaultPosition
    });
  }

  const videoQuality = VIDEO_QUALITY.FULL_HD;
  const height = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].HEIGHT;
  const width = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].WIDTH;

  const fps = getClipFPS(clip);

  const showCaptions = !!clip.asset_metadata.subtitle || isClipAudiogram(clip);
  const segmentsTrackValue = clip.asset_metadata.player_config?.playerConfig.tracks.find(
    track => track.type === TrackType.SEGMENTS
  ) as SegmentsTrack;

  const segmentsTrack: SegmentsTrack = {
    id: MAIN_TRACK_ID,
    name: 'Segments Track',
    type: TrackType.SEGMENTS,
    hideTrack: {
      inTimeline: false,
      inPlayer: false
    },
    segments: [],
    segmentTransitions: segmentsTrackValue?.segmentTransitions || []
  };
  const existingCompositeSegment = segmentsTrackValue?.segments.find(
    segment => segment.type === SegmentType.COMPOSITE
  ) as CompositeSegment;

  let durationInFrames = 0;
  const introDuration = Math.ceil((clip.asset_metadata.introDuration || 0) * fps);
  if (clip.asset_metadata.intro && introDuration) {
    segmentsTrack.segments.push(createIntroVideoSegment(clip, introDuration));
    durationInFrames += introDuration;
  }

  const compositeSegmentDuration = Math.floor((clip.asset_metadata?.duration || 0) * fps);
  let maxBounds = existingCompositeSegment?.maxBounds || {
    fromFrame: 0,
    toFrame: clip.asset_metadata.end * fps
  };

  if (isNotFullLengthClip(clip) && typeof clip.asset_metadata.maxVideoDuration === 'number') {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }

  if (
    typeof clip.asset_metadata.maxVideoDuration === 'number' &&
    clip.asset_metadata.maxVideoDuration > clip.asset_metadata.end &&
    isFullLengthClip(clip)
  ) {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }

  const compositeSegment = createMainCompositeSegment({
    clip,
    fps,
    duration: compositeSegmentDuration,
    offset: durationInFrames,
    sceneOverrides,
    facialRecognitionToDefaultPosition
  });

  // Update effects if there are deletes
  const previousClip = currentClip.getSnapshot()[clip.id];
  if (existingCompositeSegment?.effects) {
    compositeSegment.effects = existingCompositeSegment?.effects;
  }

  // Compute playback-adjusted effects for the composite segment
  const playbackAdjustedEffects = mapEffectsToPlayableTimeline(
    compositeSegment.effects,
    compositeSegment.playRanges,
    fps
  );

  const playbackAdjustedScenes = mapScenesToPlayableTimeline(compositeSegment.scenes, compositeSegment.playRanges);

  segmentsTrack.segments.push({
    ...compositeSegment,
    playbackAdjustedEffects,
    playbackAdjustedScenes,
    maxBounds
  });
  durationInFrames += compositeSegmentDuration;

  const outroDuration = Math.ceil((clip.asset_metadata.outroDuration || 0) * fps);
  if (clip.asset_metadata.outro && outroDuration) {
    segmentsTrack.segments.push(createOutroVideoSegment(clip, outroDuration, durationInFrames));
    durationInFrames += outroDuration;
  }

  let tracks: Track[] = [];
  if (segmentsTrack.segments.length) {
    const fontObject = getFontObject({ clip, fontTarget: SelectionElementType.CAPTIONS });
    const existingElementsTracks = getPlayerConfigElements(clip?.asset_metadata?.player_config);

    // Update element tracks with deletes first
    let updatedElementsTracks: ElementsTrack[] = [];
    if (existingElementsTracks) {
      updatedElementsTracks = updatePlayerConfigWithDeletes(existingElementsTracks, fps, previousClip, clip);
    }

    let pages: Page[] = [];
    let metadata: CaptionTrackMetadata | undefined = undefined;

    if (canUseBrowserlessCaptions()) {
      metadata = buildCaptionMetadata(clip);
    } else {
      pages = getCaptionPages({
        clip,
        updatedElementTracks: updatedElementsTracks,
        captionTrackOffset: compositeSegment.fromFrame
      });
    }

    tracks = [
      {
        segmentId: 'random',
        name: 'Captions',
        id: 'captions',
        type: TrackType.CAPTIONS,
        hideTrack: {
          inPlayer: !showCaptions,
          inTimeline: true
        },
        pages,
        metadata,
        fromFrame: compositeSegment.fromFrame,
        toFrame: compositeSegment.toFrame,
        ...fontObject
      }
    ];

    tracks.unshift(...updatedElementsTracks);
    tracks.unshift(segmentsTrack);
  } else {
    tracks = clip.asset_metadata.player_config?.playerConfig?.tracks || [];
  }

  return {
    height,
    width,
    fps,
    durationInFrames,
    version: '0.0.1',
    playerConfig: {
      tracks
    }
  };
}
